import { ReactNode } from "react";
import { easeLoading, filter, map, forkJoin, switchMap, of, catchError, Observable, share, tap } from "@pjs/observables";
import {
    getTemporaryAccessAdmins,
    getTemporaryAccessSettings,
    TemporaryAccessState,
    temporaryAccessDetails,
    ITemporaryAccessSettings,
    ITemporaryAccessAdmin
} from "@pjs/user-domain";
import { actionLogger } from "@pjs/a11y";
import { AsyncModel, createAsyncModelError, createAsyncModelPending, createAsyncModelResult, IAsyncModelError } from "@pjs/utilities";
import { errorNotice, LoadingSpinner, successNotice, temporaryAccessIcon } from "@pjs/core-ui";
import { userUiI18n } from "../../i18n/UserUiI18n.const";
import { TemporaryAccessForm } from "../../temporary-access/TemporaryAccessForm.component";
import { TemporaryAccessDetails } from "../../temporary-access/TemporaryAccessDetails.component";
import { View } from "../enums/View";
import { IViewConfig } from "../interfaces/IViewConfig";
import { AccountSettingsError } from "../AccountSettingsError.component";

const createErrorView = (): Observable<IAsyncModelError<ReactNode>> => {
    const title = userUiI18n.getString("settings_view.loading_error.title");
    const content = userUiI18n.getString("settings_view.loading_error.content");
    const loggerMessage = userUiI18n.getString("temporary_access.revoke_access.logger.page_load_error");
    actionLogger.append(loggerMessage);

    return of(createAsyncModelError(<AccountSettingsError title={title} content={content} />));
};

const formDataCache: {
    settings: ITemporaryAccessSettings | null;
    admins: Array<ITemporaryAccessAdmin> | null;
    getData(): Observable<[ITemporaryAccessSettings, Array<ITemporaryAccessAdmin>]>;
    clear(): void;
} = {
    settings: null,
    admins: null,
    getData() {
        if (this.settings !== null && this.admins !== null) {
            return of([this.settings, this.admins]);
        }

        return forkJoin([getTemporaryAccessSettings(), getTemporaryAccessAdmins()]).pipe(
            tap(([settings, admins]) => {
                this.admins = admins;
                this.settings = settings;
            })
        );
    },
    clear: () => {
        formDataCache.admins = null;
        formDataCache.settings = null;
    }
};

export const createTemporaryAccessView = (isTemporaryAccessSession: boolean): IViewConfig => {
    return {
        id: View.TemporaryAccess,
        page: {
            get title() {
                return userUiI18n.getString("temporary_access.title");
            },
            get description() {
                return userUiI18n.getString("temporary_access.description");
            }
        },
        navigation: {
            icon: temporaryAccessIcon,
            get label() {
                return userUiI18n.getString("settings_sidebar.buttons.temporary_access");
            }
        },
        component: () => {
            const getData = temporaryAccessDetails.load().pipe(
                switchMap((model) => {
                    if (model.details.id !== "") {
                        return of(createAsyncModelResult(<TemporaryAccessDetails isTemporaryAccessSession={isTemporaryAccessSession} model={model} />));
                    }

                    return formDataCache.getData().pipe(
                        map(([settings, admins]) => {
                            return createAsyncModelResult(
                                <TemporaryAccessForm settings={settings} admins={admins} modelState={model.state} clearFormData={() => formDataCache.clear()} />
                            );
                        }),
                        catchError(() => {
                            return createErrorView();
                        })
                    );
                }),
                catchError(() => {
                    return createErrorView();
                }),
                share()
            );

            return easeLoading<AsyncModel<ReactNode, ReactNode, ReactNode>>(
                createAsyncModelPending(<LoadingSpinner className="uui-settings-view__loading-spinner" />),
                getData
            );
        },
        isEnabled: (userDetails, _accountSettings) => !userDetails.isExternal,
        state: () => {
            return temporaryAccessDetails.get().model.state.pipe(
                filter((state) => {
                    return (
                        state.descriptor === TemporaryAccessState.RevokeFailed ||
                        state.descriptor === TemporaryAccessState.ExtendFailed ||
                        state.descriptor === TemporaryAccessState.GrantFailed ||
                        state.descriptor === TemporaryAccessState.ExtendSuccess
                    );
                }),
                map((state) => {
                    if (state.descriptor === TemporaryAccessState.RevokeFailed) {
                        const label = userUiI18n.getString("temporary_access.revoke_access.error");

                        return {
                            ariaLabel: label,
                            type: errorNotice,
                            message: label,
                            actionLogMessage: label
                        };
                    }

                    if (state.descriptor === TemporaryAccessState.ExtendFailed) {
                        const label = userUiI18n.getString("temporary_access.extend_access.error");

                        return {
                            ariaLabel: label,
                            type: errorNotice,
                            message: label,
                            actionLogMessage: label
                        };
                    }

                    if (state.descriptor === TemporaryAccessState.GrantFailed) {
                        const label = userUiI18n.getString("temporary_access.grant_access.error");

                        return {
                            ariaLabel: label,
                            type: errorNotice,
                            message: label,
                            actionLogMessage: label
                        };
                    }

                    const label = userUiI18n.getString("temporary_access.extend_access.success");

                    return {
                        ariaLabel: label,
                        type: successNotice,
                        message: label,
                        actionLogMessage: label
                    };
                })
            );
        },
        clear: () => temporaryAccessDetails.clear()
    };
};
